import { useContext } from "react"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"
import FormikTextAreaInput from "../../../helpers/formikInputs/FormikTextAreaInput"
import FormikNumberInput from "../../../helpers/formikInputs/FormikNumberInput"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//primeReact
import { Dialog } from "primereact/dialog"
import { Card } from "primereact/card"

//Housing components
import {
  housingApiCreateRentPaymentPost,
  housingApiCreateUserChargePaymentPost,
} from "../housingApi"
import myStyles from "../../../styles/housing/HousingRent.module.css"
import { LogError } from "../../../helpers/logger"

export default function HousingRentPaymentForm({
  showForm,
  onHideForm,
  onPaymentCreated,
  rentEntry,
  participants,
}) {
  const userCtx = useContext(UserContext)

  const paymentDue =
    (rentEntry?.rent.amountDue || 0) - (rentEntry?.paymentsReceived || 0)

  const validationSchema = Yup.object().shape({
    paymentDate: Yup.date().required("The payment date is required"),
    amountPaid: Yup.number()
      .min(0.01)
      .max(
        paymentDue,
        "The payment amount is more than the outstanding rent amount",
      )
      .required("Please enter the payment amount"),
  })

  const submitPayment = async (values) => {
    try {
      let result = undefined

      if (!!values.isRent) {
        result = await housingApiCreateRentPaymentPost(
          userCtx.tokens.getHousingToken(),
          values,
        )
      } else {
        let submitValues = { ...values, userChargeId: values.rentId }

        result = await housingApiCreateUserChargePaymentPost(
          userCtx.tokens.getHousingToken(),
          submitValues,
        )
      }

      if (!!result) {
        onPaymentCreated()
      }

      return true
    } catch (error) {
      LogError("Unable to submit payment", error)
      userCtx.setNotificationError("Unable to submit payment")
      return false
    }
  }

  const formatCurrency = (value) => {
    if (value !== undefined) {
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })
    } else {
      return ""
    }
  }

  const participantName = () => {
    let name = "Unknown"
    if (!!participants) {
      const rowParticipant = participants.filter(
        (participant) =>
          participant.participant_id === rentEntry?.rent.participantId,
      )

      if (!!rowParticipant && rowParticipant.length > 0) {
        name = rowParticipant[0].name2
      }
    }

    return name
  }

  const outstandingPayment = () => {
    return formatCurrency(
      (rentEntry?.rent.amountDue || 0) - (rentEntry?.paymentsReceived || 0),
    )
  }

  return (
    <Dialog
      header={`New Payment`}
      visible={showForm}
      onHide={() => {
        onHideForm()
      }}
      style={{ width: "400px" }}
    >
      <Card className={myStyles.paymentRentCard}>
        <div className={myStyles.paymentRentGridContainer}>
          <div className={myStyles.paymentRentGridItemHouse}>Participant:</div>
          <div className={myStyles.paymentRentGridItemHouseValue}>
            {participantName()}
          </div>
          <div className={myStyles.paymentRentGridItemRent}>Rent due:</div>
          <div className={myStyles.paymentRentGridItemRentValue}>
            {formatCurrency(rentEntry?.rent.amountDue)}
          </div>
          <div className={myStyles.paymentRentGridItemPayments}>
            Payments received:
          </div>
          <div className={myStyles.paymentRentGridItemPaymentsValue}>
            {formatCurrency(rentEntry?.paymentsReceived)}
          </div>
          <div className={myStyles.paymentRentGridItemStatus}>Status: </div>
          <div
            style={{
              color: rentEntry?.status === "Overdue" ? "red" : "black",
            }}
            className={myStyles.paymentRentGridItemStatusValue}
          >
            {rentEntry?.status}
          </div>
        </div>
      </Card>
      <Formik
        initialValues={{
          rentId: rentEntry?.rent.id,
          isRent: rentEntry?.rent.isRent,
          paymentDate: new Date(),
          userId: userCtx.session.user.staff_id,
          amountPaid: 0,
          note: undefined,
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          return submitPayment(values)
        }}
      >
        {({
          isSubmitting,
          values,
          dirty,
          setFieldValue,
          setErrors,
          errors,
        }) => (
          <Form>
            <FormikCalendar
              name={"paymentDate"}
              disabled={isSubmitting}
              helpText={"The date that the payment was received"}
              label={"Payment Date"}
              required={true}
            />
            <FormikNumberInput
              currencyCode={"USD"}
              name={"amountPaid"}
              helpText={`The amount paid (amout due before payment: ${outstandingPayment()})`}
              label={"Payment amount"}
              required={true}
            />
            <FormikTextAreaInput
              name={"note"}
              label={"Notes"}
              disabled={isSubmitting}
              helpText={"Notes pertaining to the payment"}
              rows={5}
            />
            <div style={{ float: "right" }}>
              <button
                type="submit"
                className="btn"
                disabled={isSubmitting || !dirty}
              >
                Save
              </button>
              <button
                type="button"
                className="btn"
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  onHideForm()
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
